import React, { useEffect, useState } from 'react';
import { useDrag } from 'react-dnd';
import { Box, Typography, Chip, Tooltip, Skeleton, IconButton, Menu, MenuItem } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ITEM_TYPE } from './Viewport';
import { getSeriesRenderUrl, getSeriesThumbnailUrl } from './seriesThumbnail';
import { buildSeriesDragItem, formatIngestProgressLabel, getSeriesThumbnailSlice, KAIROS_QOL_INGEST_PROGRESS_MARKER } from './seriesQol';
import { buildKeyObjectVirtualSeries, KAIROS_QOL_KEY_OBJECT_SERIES_MARKER } from './keyObjectSeries';
import { KAIROS_SERIES_NON_IMAGE_THUMBNAIL_GUARD, isRenderableSeriesThumbnail } from './seriesRenderability';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(raw) {
  if (!raw || raw.length < 8) return raw || '';
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
}

function formatName(raw) {
  if (!raw) return 'Unknown Patient';
  // DICOM name format: Last^First^Middle → "First Last"
  const parts = raw.split('^').map(p => p.trim()).filter(Boolean);
  if (parts.length >= 2) return `${parts[1]} ${parts[0]}`;
  return parts[0] || raw;
}

// ─── Series thumbnail card ────────────────────────────────────────────────────

const SeriesItem = ({ orientation = 'vertical', series, onViewDicomTags, contextMenu, onOpenContextMenu, onCloseContextMenu, isKeyObject = false }) => {
  const horizontal = orientation === 'horizontal';
  const [thumbLoaded, setThumbLoaded] = useState(false);
  const [thumbError, setThumbError] = useState(false);

  const closeContextMenu = () => onCloseContextMenu?.();
  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onOpenContextMenu?.(series, e.clientX + 2, e.clientY - 6);
  };
  const handleViewDicomTags = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    closeContextMenu();
    onViewDicomTags && onViewDicomTags(series);
  };

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: buildSeriesDragItem(series),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const seriesUID = series.seriesInstanceUID || series.series_instance_uid || series.seriesUID || series.series_uid;
  const seriesDescription = series.seriesDescription || series.SeriesDescription || series.description || '';
  const canRenderThumbnail = isRenderableSeriesThumbnail(series);
  const thumbSlice = getSeriesThumbnailSlice(series);
  const ingestProgressLabel = formatIngestProgressLabel(series);
  const studyUID = series.studyInstanceUID || series.study_instance_uid || series.studyUID || series.study_uid;
  const thumbUrl = canRenderThumbnail ? getSeriesThumbnailUrl(studyUID, seriesUID, thumbSlice, 160) : '';
  const fallbackUrl = canRenderThumbnail ? getSeriesRenderUrl(studyUID, seriesUID, thumbSlice) : '';
  const [imageSrc, setImageSrc] = useState(thumbUrl);

  useEffect(() => {
    setThumbLoaded(false);
    setThumbError(false);
    setImageSrc(thumbUrl);
  }, [thumbUrl]);

  return (
    <Tooltip title={`Drag to viewport opens first image${isKeyObject ? ' / marked image' : ''} • Right-click for DICOM Tags / Fields • ${series.numInstances || series.displayFrameCount || ''} instances${ingestProgressLabel ? ` • ${ingestProgressLabel}` : ''}`} placement="right">
      <Box
        ref={drag}
        data-testid="series-thumbnail-card"
        data-series-instance-uid={seriesUID || ''}
        data-series-description={seriesDescription}
        data-series-modality={series.modality || series.Modality || ''}
        data-thumbnail-loaded={thumbLoaded ? 'true' : 'false'}
        data-drag-behavior={series._kairosDragBehavior || ''}
        onContextMenu={handleContextMenu}
        sx={{
          mb: 0.5,
          ...(horizontal ? { mb: 0, height: '100%', display: 'flex', flexDirection: 'column' } : {}),
          border: '1px solid',
          borderColor: isDragging ? '#00bcd4' : '#222',
          bgcolor: isDragging ? '#002233' : '#0d0d0d',
          cursor: 'grab',
          opacity: isDragging ? 0.6 : 1,
          transition: 'border-color 0.12s, background-color 0.12s',
          '&:hover': { borderColor: '#00bcd4', bgcolor: '#0a1a20' },
          '&:hover .series-context-hint': { opacity: 1 },
          userSelect: 'none',
        }}
      >
        {/* Thumbnail */}
        <Box sx={{ position: 'relative', width: '100%', aspectRatio: '1 / 1', bgcolor: '#000', overflow: 'hidden', ...(horizontal ? { aspectRatio: 'auto', flex: 1, minHeight: 0 } : {}) }}>
          {canRenderThumbnail && !thumbLoaded && !thumbError && (
            <Skeleton variant="rectangular" width="100%" height="100%" sx={{ bgcolor: '#1a1a1a' }} />
          )}
          {canRenderThumbnail && !thumbError && (
            <img
              src={imageSrc}
              alt={seriesDescription}
              draggable={false}
              loading="eager"
              decoding="async"
              fetchPriority="low"
              onLoad={() => setThumbLoaded(true)}
              onError={() => {
                if (imageSrc !== fallbackUrl) {
                  setImageSrc(fallbackUrl);
                  return;
                }
                setThumbError(true);
              }}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: thumbLoaded ? 'block' : 'none',
              }}
            />
          )}
          {!canRenderThumbnail && (
            <Box data-kairos-marker={KAIROS_SERIES_NON_IMAGE_THUMBNAIL_GUARD} sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 0.5, color: '#3a4b55' }}>
              <Typography sx={{ fontSize: 10, color: '#5b7480', fontWeight: 700, letterSpacing: 1 }}>NON-IMAGE</Typography>
              <Typography sx={{ fontSize: 9, color: '#3f5963' }}>{series.modality || 'DATA'}</Typography>
            </Box>
          )}
          {canRenderThumbnail && thumbError && (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: 9, color: '#333' }}>NO PREVIEW</Typography>
            </Box>
          )}

          {/* Modality badge — top left */}
          <Box sx={{
            position: 'absolute', top: 4, left: 4,
            bgcolor: 'rgba(0,51,102,0.85)',
            border: '1px solid #00bcd4',
            px: 0.5, py: 0.1,
            lineHeight: 1,
          }}>
            <Typography sx={{ fontSize: 9, color: '#00bcd4', fontWeight: 'bold', letterSpacing: 1 }}>
              {series.modality || '??'}
            </Typography>
          </Box>

          {/* Right-click affordance — top right */}
          <Box className="series-context-hint" sx={{
            position: 'absolute', top: 4, right: 4, opacity: 0, transition: 'opacity 0.12s',
            bgcolor: 'rgba(0,0,0,0.55)', border: '1px solid rgba(0,188,212,0.35)', px: 0.5,
          }}>
            <Typography sx={{ fontSize: 8, color: '#00bcd4', fontFamily: 'monospace' }}>RIGHT-CLICK</Typography>
          </Box>

          {ingestProgressLabel && (
            <Box data-kairos-marker={KAIROS_QOL_INGEST_PROGRESS_MARKER} sx={{ position: 'absolute', bottom: 22, left: 4, right: 4, bgcolor: 'rgba(255,179,0,0.88)', border: '1px solid rgba(255,255,255,0.55)', px: 0.45, py: 0.15, textAlign: 'center', boxShadow: '0 0 10px rgba(255,179,0,0.28)' }}>
              <Typography sx={{ fontSize: 8, color: '#180f00', fontFamily: 'monospace', fontWeight: 900, letterSpacing: 0.7 }}>{ingestProgressLabel}</Typography>
            </Box>
          )}

          {/* Instance count — bottom right */}
          <Box sx={{
            position: 'absolute', bottom: 4, right: 4,
            bgcolor: 'rgba(0,0,0,0.6)',
            px: 0.5,
          }}>
            <Typography sx={{ fontSize: 9, color: '#888', fontFamily: 'monospace' }}>
              {series.seriesNumber}/{series.numInstances}
            </Typography>
          </Box>
        </Box>

        {/* Description row */}
        <Box sx={{ px: 1, py: 0.6, display: 'flex', alignItems: 'center', gap: 0.5, ...(horizontal ? { flexShrink: 0 } : {}) }}>
          <Typography title={horizontal ? seriesDescription : undefined} sx={{
            flex: 1, minWidth: 0, fontSize: 10, color: '#bbb', lineHeight: 1.3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {series.seriesDescription || 'No description'}
          </Typography>
          <Tooltip title="View DICOM Tags / Fields">
            <IconButton
              size="small"
              onClick={handleViewDicomTags}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{ p: 0.25, color: '#455a64', '&:hover': { color: '#00bcd4', bgcolor: 'rgba(0,188,212,0.08)' } }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Menu
          open={contextMenu?.seriesInstanceUID === series.seriesInstanceUID}
          onClose={closeContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={contextMenu?.seriesInstanceUID === series.seriesInstanceUID ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
          onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
          PaperProps={{
            sx: {
              bgcolor: '#111',
              color: '#ddd',
              border: '1px solid #263238',
              minWidth: 170,
              boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
            },
          }}
        >
          <MenuItem
            dense
            onClick={handleViewDicomTags}
            sx={{ fontSize: 12, color: '#b7dce5', '&:hover': { bgcolor: 'rgba(0,188,212,0.12)' } }}
          >
            View DICOM Tags / Fields
          </MenuItem>
        </Menu>
      </Box>
    </Tooltip>
  );
};

// ─── Patient / study header ───────────────────────────────────────────────────

const StudyHeader = ({ studyInfo, orientation = 'vertical' }) => {
  const horizontal = orientation === 'horizontal';
  if (!studyInfo) {
    return (
      <Box sx={{ px: 1.5, py: 1, width: horizontal ? 220 : 'auto', flexShrink: 0, borderRight: horizontal ? '1px solid #1e1e1e' : 'none', borderBottom: horizontal ? 'none' : '1px solid #1e1e1e' }}>
        <Skeleton variant="text" width="70%" sx={{ bgcolor: '#1e1e1e' }} />
        <Skeleton variant="text" width="50%" sx={{ bgcolor: '#1a1a1a', mt: 0.5 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 1.5, py: 1, width: horizontal ? 220 : 'auto', flexShrink: 0, overflow: 'hidden', borderRight: horizontal ? '1px solid #1e1e1e' : 'none', borderBottom: horizontal ? 'none' : '1px solid #1e1e1e' }}>
      {/* Patient name */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4 }}>
        <Typography sx={{ fontSize: 12, color: '#e0e0e0', fontWeight: 'bold', lineHeight: 1.2 }}>
          {formatName(studyInfo.patientName)}
        </Typography>
      </Box>

      {/* Modality + study description */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3, flexWrap: 'wrap' }}>
        {studyInfo.modalities && (
          <Chip
            label={studyInfo.modalities}
            size="small"
            sx={{ fontSize: 9, height: 14, bgcolor: '#003366', color: '#00bcd4', borderRadius: 0, minWidth: 24 }}
          />
        )}
        {studyInfo.studyDescription && (
          <Typography sx={{ fontSize: 10, color: '#aaa', lineHeight: 1, ...(horizontal ? { display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden' } : {}) }}>
            {studyInfo.studyDescription}
          </Typography>
        )}
      </Box>

      {/* Study date */}
      {studyInfo.studyDate && (
        <Typography sx={{ fontSize: 9, color: '#555', fontFamily: 'monospace' }}>
          {formatDate(studyInfo.studyDate)}
        </Typography>
      )}

      {/* Drag hint */}
      <Typography sx={{ fontSize: 8, color: '#333', mt: 0.5, letterSpacing: 1 }}>
        DRAG TO VIEWPORT
      </Typography>
    </Box>
  );
};

// ─── Panel ────────────────────────────────────────────────────────────────────

const SeriesPanel = ({ orientation = 'vertical', series, loading, studyInfo, onViewDicomTags, keyObjects = [], onClearKeyObjects }) => {
  const [contextMenu, setContextMenu] = useState(null);
  const horizontal = orientation === 'horizontal';
  const openContextMenu = (s, mouseX, mouseY) => setContextMenu({ seriesInstanceUID: s.seriesInstanceUID, mouseX, mouseY });
  const closeContextMenu = () => setContextMenu(null);

  return (
    <Box
      data-series-panel-orientation={orientation}
      onContextMenu={(e) => e.preventDefault()}
      sx={{
        width: '100%', height: '100%', display: 'flex', flexDirection: horizontal ? 'row' : 'column',
        bgcolor: '#111', borderRight: horizontal ? 'none' : '1px solid #1e1e1e',
        borderBottom: horizontal ? '1px solid #1e1e1e' : 'none',
      }}
    >
      <StudyHeader studyInfo={studyInfo} orientation={orientation} />

      <Box sx={{
        flex: 1, minWidth: 0, minHeight: 0, p: 0.75,
        overflowX: horizontal ? 'auto' : 'hidden', overflowY: horizontal ? 'hidden' : 'auto',
        display: horizontal ? 'flex' : 'block', gap: horizontal ? 0.75 : 0,
      }}>
        {loading && (
          <>
            {[1, 2, 3].map(i => (
              <Box key={i} sx={{ mb: horizontal ? 0 : 0.5, width: horizontal ? 156 : 'auto', flexShrink: 0 }}>
                <Skeleton variant="rectangular" width="100%" height={horizontal ? 112 : 140} sx={{ bgcolor: '#1a1a1a' }} />
                <Skeleton variant="text" width="80%" sx={{ bgcolor: '#1a1a1a', mt: 0.5 }} />
              </Box>
            ))}
          </>
        )}
        {!loading && series.length === 0 && (
          <Typography sx={{ color: '#444', fontSize: 11, textAlign: 'center', mt: 2 }}>No series</Typography>
        )}
        {keyObjects.length > 0 && (
          <Box data-kairos-marker={KAIROS_QOL_KEY_OBJECT_SERIES_MARKER} sx={{ mb: horizontal ? 0 : 1, pb: horizontal ? 0 : 0.75, width: horizontal ? 160 : 'auto', height: horizontal ? '100%' : 'auto', flexShrink: 0, overflowY: horizontal ? 'auto' : 'visible', borderRight: horizontal ? '1px solid rgba(255,235,59,0.18)' : 'none', borderBottom: horizontal ? 'none' : '1px solid rgba(255,235,59,0.18)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ color: '#fff59d', fontSize: 9, fontFamily: 'monospace', fontWeight: 900, letterSpacing: 1 }}>KEY OBJECT SERIES</Typography>
              <Typography onClick={onClearKeyObjects} sx={{ color: '#665f22', fontSize: 8, fontFamily: 'monospace', cursor: 'pointer', '&:hover': { color: '#fff59d' } }}>CLEAR</Typography>
            </Box>
            {keyObjects.map(mark => (
              <SeriesItem key={mark.id} orientation={orientation} series={buildKeyObjectVirtualSeries(mark)} isKeyObject onViewDicomTags={onViewDicomTags} contextMenu={contextMenu} onOpenContextMenu={openContextMenu} onCloseContextMenu={closeContextMenu} />
            ))}
          </Box>
        )}
        {series.map(s => (
          <Box key={s.seriesInstanceUID} sx={{ width: horizontal ? 156 : 'auto', height: horizontal ? '100%' : 'auto', flexShrink: 0 }}>
            <SeriesItem series={s} orientation={orientation} onViewDicomTags={onViewDicomTags} contextMenu={contextMenu} onOpenContextMenu={openContextMenu} onCloseContextMenu={closeContextMenu} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SeriesPanel;
